import { useEffect, useRef } from 'react';
import type { FormFieldChange, HandleChangesType } from '../../form/FormContext';
import type { Reference } from '../../reference/reference';

type DefaultContactFields = {
    organizationReference?: Reference;
    role?: string;
};

export type GetDefaultContactFieldsForPerson = (
    personId: string
) => Promise<DefaultContactFields>;

type UseRepresentativeDefaultPrefillProps = {
    currentOrganizationReference?: Reference;
    currentRole?: string;
    getDefaultContactFieldsForPerson?: GetDefaultContactFieldsForPerson;
    handleChanges: HandleChangesType;
    selectedPersonId?: string;
    showOrganization: boolean;
};

export const useRepresentativeDefaultPrefill = ({
    currentOrganizationReference,
    currentRole,
    getDefaultContactFieldsForPerson,
    handleChanges,
    selectedPersonId,
    showOrganization
}: UseRepresentativeDefaultPrefillProps) => {
    const initialSelectedPersonId = useRef<string | undefined>(selectedPersonId);
    const previousSelectedPersonId = useRef<string | undefined>(selectedPersonId);
    const hasSelectedPersonChanged = useRef(false);
    const lastPrefilledPersonId = useRef<string | undefined>(undefined);
    const prefillInProgressPersonId = useRef<string | undefined>(undefined);
    const currentOrganizationReferenceRef = useRef(currentOrganizationReference);
    const currentRoleRef = useRef(currentRole);
    const handleChangesRef = useRef(handleChanges);
    const getDefaultContactFieldsForPersonRef = useRef(getDefaultContactFieldsForPerson);

    useEffect(() => {
        currentOrganizationReferenceRef.current = currentOrganizationReference;
        currentRoleRef.current = currentRole;
        handleChangesRef.current = handleChanges;
        getDefaultContactFieldsForPersonRef.current = getDefaultContactFieldsForPerson;
    }, [currentOrganizationReference, currentRole, getDefaultContactFieldsForPerson, handleChanges]);

    useEffect(() => {
        if (!showOrganization || !getDefaultContactFieldsForPersonRef.current) {
            return;
        }

        if (selectedPersonId !== previousSelectedPersonId.current) {
            hasSelectedPersonChanged.current = true;
            previousSelectedPersonId.current = selectedPersonId;
        }

        if (!selectedPersonId) {
            lastPrefilledPersonId.current = undefined;
            return;
        }

        if (
            selectedPersonId === lastPrefilledPersonId.current ||
            selectedPersonId === prefillInProgressPersonId.current
        ) {
            return;
        }

        let isCurrent = true;
        prefillInProgressPersonId.current = selectedPersonId;

        getDefaultContactFieldsForPersonRef
            .current(selectedPersonId)
            .then(({ organizationReference, role }) => {
                if (!isCurrent) {
                    return;
                }

                const shouldPreserveInitialRepresentativeFields =
                    selectedPersonId === initialSelectedPersonId.current && !hasSelectedPersonChanged.current;
                const organizationShouldBePrefilled =
                    !shouldPreserveInitialRepresentativeFields || !currentOrganizationReferenceRef.current?.id;
                const roleShouldBePrefilled = !shouldPreserveInitialRepresentativeFields || !currentRoleRef.current;

                const prefillChanges: FormFieldChange[] = [];

                if (organizationShouldBePrefilled) {
                    prefillChanges.push({
                        prop: 'organizationReference',
                        value: organizationReference || undefined
                    });
                }

                if (roleShouldBePrefilled) {
                    prefillChanges.push({
                        prop: 'role',
                        value: role || undefined
                    });
                }

                if (!prefillChanges.length) {
                    lastPrefilledPersonId.current = selectedPersonId;
                    return;
                }

                handleChangesRef.current(prefillChanges);

                lastPrefilledPersonId.current = selectedPersonId;
            })
            .catch((error) => {
                if (prefillInProgressPersonId.current === selectedPersonId) {
                    prefillInProgressPersonId.current = undefined;
                }
                console.warn('Failed to fetch contact defaults:', error);
            });

        return () => {
            isCurrent = false;
            if (prefillInProgressPersonId.current === selectedPersonId) {
                prefillInProgressPersonId.current = undefined;
            }
        };
    }, [selectedPersonId, showOrganization]);
};
